import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Minus,
  Maximize2,
  Minimize2,
  UserCheck,
  AlertTriangle,
  Sparkles,
  X,
} from "lucide-react";
import type { HiveWorkflow, WfNode } from "@/types";
import { useWorkflow } from "@/store/workflowStore";
import {
  nodeDef,
  NODE_W,
  NODE_H,
  LANE_X,
  KIND_META,
  INTEGRATION_META,
} from "@/data/mockWorkflowNodes";
import { RISK_META } from "@/components/hive/shared";
import { clamp } from "@/lib/utils";

const LANES = [
  { kind: "hook" as const, x: -40, w: 420 },
  { kind: "process" as const, x: 420, w: 700 },
  { kind: "publish" as const, x: 1130, w: 460 },
];

function anchorOut(n: WfNode) {
  return { x: n.x + NODE_W, y: n.y + NODE_H / 2 };
}
function anchorIn(n: WfNode) {
  return { x: n.x, y: n.y + NODE_H / 2 };
}
function bezier(x1: number, y1: number, x2: number, y2: number) {
  const dx = Math.max(50, Math.abs(x2 - x1) * 0.5);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

const EDGE_TONE: Record<string, string> = {
  default: "#64748b",
  success: "#22c55e",
  fail: "#f43f5e",
  approval: "#a855f7",
  error: "#f43f5e",
  timeout: "#f59e0b",
};

export function WorkflowCanvas({
  wf,
  fullscreen,
  onToggleFullscreen,
}: {
  wf: HiveWorkflow;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
}) {
  const selectedNodeId = useWorkflow((s) => s.selectedNodeId);
  const selectNode = useWorkflow((s) => s.selectNode);
  const moveNode = useWorkflow((s) => s.moveNode);
  const connect = useWorkflow((s) => s.connect);
  const deleteEdge = useWorkflow((s) => s.deleteEdge);

  const wrapRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ x: 60, y: 20 });
  const [zoom, setZoom] = useState(0.82);
  const [connecting, setConnecting] = useState<{ source: string; wx: number; wy: number } | null>(null);

  const nodeById = new Map(wf.nodes.map((n) => [n.id, n]));

  const toWorld = useCallback(
    (clientX: number, clientY: number) => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: (clientX - rect.left - pan.x) / zoom, y: (clientY - rect.top - pan.y) / zoom };
    },
    [pan, zoom]
  );

  /* ── Pan (background drag) + wheel ──────────────────────────────────────── */
  const panRef = useRef({ active: false, px: 0, py: 0, ox: 0, oy: 0 });
  function onBgPointerDown(e: React.PointerEvent) {
    if (e.button !== 0) return;
    selectNode(null);
    panRef.current = { active: true, px: e.clientX, py: e.clientY, ox: pan.x, oy: pan.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onBgPointerMove(e: React.PointerEvent) {
    if (!panRef.current.active) return;
    setPan({ x: panRef.current.ox + (e.clientX - panRef.current.px), y: panRef.current.oy + (e.clientY - panRef.current.py) });
  }
  function onBgPointerUp(e: React.PointerEvent) {
    panRef.current.active = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  }

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        setZoom((z) => {
          const nz = clamp(z * (1 - e.deltaY * 0.0016), 0.4, 1.7);
          setPan((p) => ({
            x: cx - ((cx - p.x) / z) * nz,
            y: cy - ((cy - p.y) / z) * nz,
          }));
          return nz;
        });
      } else {
        setPan((p) => ({ x: p.x - e.deltaX, y: p.y - e.deltaY }));
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // When mounted inside the fullscreen modal, frame the whole workflow once laid out.
  useEffect(() => {
    if (!fullscreen) return;
    const t = setTimeout(() => fit(), 70);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen]);

  /* ── Connection drag ────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!connecting) return;
    const onMove = (e: PointerEvent) => {
      const w = toWorld(e.clientX, e.clientY);
      setConnecting((c) => (c ? { ...c, wx: w.x, wy: w.y } : c));
    };
    const onUp = (e: PointerEvent) => {
      const w = toWorld(e.clientX, e.clientY);
      const target = wf.nodes.find(
        (n) =>
          n.id !== connecting.source &&
          w.x >= n.x - 14 &&
          w.x <= n.x + NODE_W &&
          w.y >= n.y - 6 &&
          w.y <= n.y + NODE_H + 6
      );
      if (target) connect(connecting.source, target.id);
      setConnecting(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [connecting, toWorld, wf.nodes, connect]);

  /* ── Node drag ──────────────────────────────────────────────────────────── */
  const dragRef = useRef<{ id: string; px: number; py: number; ox: number; oy: number } | null>(null);
  function onNodePointerDown(e: React.PointerEvent, n: WfNode) {
    e.stopPropagation();
    selectNode(n.id);
    dragRef.current = { id: n.id, px: e.clientX, py: e.clientY, ox: n.x, oy: n.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onNodePointerMove(e: React.PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    moveNode(d.id, d.ox + (e.clientX - d.px) / zoom, d.oy + (e.clientY - d.py) / zoom);
  }
  function onNodePointerUp(e: React.PointerEvent) {
    dragRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  }

  function fit() {
    const el = wrapRef.current;
    if (!el || wf.nodes.length === 0) return;
    const minX = Math.min(...wf.nodes.map((n) => n.x)) - 60;
    const minY = Math.min(...wf.nodes.map((n) => n.y)) - 40;
    const maxX = Math.max(...wf.nodes.map((n) => n.x + NODE_W)) + 60;
    const maxY = Math.max(...wf.nodes.map((n) => n.y + NODE_H)) + 40;
    const z = clamp(Math.min(el.clientWidth / (maxX - minX), el.clientHeight / (maxY - minY)), 0.4, 1.2);
    setZoom(z);
    setPan({ x: -minX * z + (el.clientWidth - (maxX - minX) * z) / 2, y: -minY * z + 16 });
  }

  return (
    <div
      ref={wrapRef}
      className="relative h-full w-full overflow-hidden bg-[hsl(var(--bg-0))]"
      style={{ touchAction: "none", cursor: panRef.current.active ? "grabbing" : "default" }}
      onPointerDown={onBgPointerDown}
      onPointerMove={onBgPointerMove}
      onPointerUp={onBgPointerUp}
    >
      {/* dotted backdrop */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage: "radial-gradient(hsl(var(--border-strong)) 1px, transparent 1px)",
          backgroundSize: `${22 * zoom}px ${22 * zoom}px`,
          backgroundPosition: `${pan.x}px ${pan.y}px`,
        }}
      />

      {/* world */}
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
      >
        {/* lane bands */}
        {LANES.map((lane) => {
          const m = KIND_META[lane.kind];
          return (
            <div
              key={lane.kind}
              className="absolute top-0 rounded-3xl border"
              style={{
                left: lane.x,
                width: lane.w,
                height: 1400,
                borderColor: `${m.color}26`,
                background: `linear-gradient(180deg, ${m.color}0d, transparent 30%)`,
              }}
            >
              <div className="flex items-center gap-2 px-5 pt-4">
                <span className="h-2 w-2 rounded-full" style={{ background: m.color }} />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: m.color }}>
                  {m.label}s
                </span>
                <span className="text-[10px] text-app-2">· {m.verb}</span>
              </div>
            </div>
          );
        })}

        {/* edges */}
        <svg className="pointer-events-none absolute left-0 top-0 overflow-visible" width={1} height={1}>
          <defs>
            {Object.entries(EDGE_TONE).map(([k, c]) => (
              <marker key={k} id={`arrow-${k}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
              </marker>
            ))}
          </defs>
          {wf.edges.map((e) => {
            const s = nodeById.get(e.source);
            const t = nodeById.get(e.target);
            if (!s || !t) return null;
            const a = anchorOut(s);
            const b = anchorIn(t);
            const tone = e.tone ?? "default";
            const c = EDGE_TONE[tone] ?? EDGE_TONE.default;
            return (
              <path
                key={e.id}
                d={bezier(a.x, a.y, b.x, b.y)}
                fill="none"
                stroke={c}
                strokeWidth={2}
                strokeOpacity={0.7}
                markerEnd={`url(#arrow-${tone})`}
                strokeDasharray={tone === "fail" || tone === "error" ? "6 5" : undefined}
              />
            );
          })}
          {connecting &&
            (() => {
              const s = nodeById.get(connecting.source);
              if (!s) return null;
              const a = anchorOut(s);
              return (
                <path
                  d={bezier(a.x, a.y, connecting.wx, connecting.wy)}
                  fill="none"
                  stroke="#22d3ee"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
              );
            })()}
        </svg>

        {/* edge labels + delete handles */}
        {wf.edges.map((e) => {
          const s = nodeById.get(e.source);
          const t = nodeById.get(e.target);
          if (!s || !t) return null;
          const a = anchorOut(s);
          const b = anchorIn(t);
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          const tone = e.tone ?? "default";
          const c = EDGE_TONE[tone] ?? EDGE_TONE.default;
          return (
            <div
              key={`lbl-${e.id}`}
              className="group absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: mx, top: my }}
            >
              {e.label ? (
                <span
                  className="flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium"
                  style={{ color: c, borderColor: `${c}55`, background: `hsl(var(--surface))` }}
                >
                  {e.label}
                </span>
              ) : (
                <span className="h-2 w-2 rounded-full opacity-0 transition group-hover:opacity-100" style={{ background: c }} />
              )}
              <button
                onClick={(ev) => {
                  ev.stopPropagation();
                  deleteEdge(e.id);
                }}
                className="absolute -right-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-rose-500 text-white opacity-0 shadow transition group-hover:opacity-100"
                title="Delete connection"
              >
                <X size={9} />
              </button>
            </div>
          );
        })}

        {/* nodes */}
        {wf.nodes.map((n) => (
          <NodeCard
            key={n.id}
            node={n}
            selected={n.id === selectedNodeId}
            onPointerDown={(e) => onNodePointerDown(e, n)}
            onPointerMove={onNodePointerMove}
            onPointerUp={onNodePointerUp}
            onStartConnect={(e) => {
              e.stopPropagation();
              const w = toWorld(e.clientX, e.clientY);
              setConnecting({ source: n.id, wx: w.x, wy: w.y });
            }}
          />
        ))}
      </div>

      {/* controls */}
      <div
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute bottom-4 left-4 flex items-center gap-1 rounded-xl border border-app glass px-1.5 py-1 shadow-lg"
      >
        <CtrlBtn onClick={() => setZoom((z) => clamp(z - 0.12, 0.4, 1.7))} title="Zoom out">
          <Minus size={14} />
        </CtrlBtn>
        <button onClick={fit} title="Fit to view" className="px-2 text-[11px] font-medium tabular-nums text-app-1 hover:text-app">
          {Math.round(zoom * 100)}%
        </button>
        <CtrlBtn onClick={() => setZoom((z) => clamp(z + 0.12, 0.4, 1.7))} title="Zoom in">
          <Plus size={14} />
        </CtrlBtn>
        {onToggleFullscreen && (
          <>
            <div className="mx-0.5 h-4 w-px bg-app" />
            <CtrlBtn onClick={onToggleFullscreen} title={fullscreen ? "Exit fullscreen" : "Open fullscreen"}>
              {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </CtrlBtn>
          </>
        )}
      </div>

      {/* hint */}
      <div className="pointer-events-none absolute bottom-4 right-4 rounded-lg border border-app glass px-2.5 py-1.5 text-[10px] text-app-2">
        Drag handles to connect · ⌘-scroll to zoom · drag canvas to pan
      </div>
    </div>
  );
}

function CtrlBtn({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title?: string }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="grid h-6 w-6 place-items-center rounded-lg text-app-1 transition hover:bg-surface-2 hover:text-app"
    >
      {children}
    </button>
  );
}

function NodeCard({
  node,
  selected,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onStartConnect,
}: {
  node: WfNode;
  selected: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onStartConnect: (e: React.PointerEvent) => void;
}) {
  const def = nodeDef(node.defKey);
  const kind = KIND_META[def.kind];
  const integ = def.integration ? INTEGRATION_META[def.integration] : null;
  const incomplete = node.configComplete < 0.6;
  const showRisk = def.risk !== "low";

  return (
    <div
      className="absolute select-none"
      style={{ left: node.x, top: node.y, width: NODE_W, height: NODE_H }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* input handle */}
      {def.kind !== "hook" && (
        <span
          className="absolute left-0 top-1/2 z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-app-strong bg-surface"
          style={{ borderColor: `${kind.color}` }}
        />
      )}
      {/* output handle */}
      {def.kind !== "publish" && (
        <span
          onPointerDown={onStartConnect}
          className="absolute right-0 top-1/2 z-10 grid h-4 w-4 -translate-y-1/2 translate-x-1/2 cursor-crosshair place-items-center rounded-full border-2 bg-surface shadow transition hover:scale-125"
          style={{ borderColor: kind.color }}
          title="Drag to connect"
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: kind.color }} />
        </span>
      )}

      <motion.div
        className="relative h-full overflow-hidden rounded-xl border bg-surface/80 backdrop-blur-xl transition-[border-color,box-shadow] duration-150"
        style={{
          borderColor: selected ? kind.color : "hsl(var(--border))",
          boxShadow: selected
            ? `0 0 0 1.5px ${kind.color}, 0 12px 32px -12px ${kind.color}99`
            : "0 8px 24px -16px rgba(0,0,0,0.5)",
          opacity: node.disabled ? 0.5 : 1,
        }}
        whileHover={{ y: -1 }}
      >
        {/* left accent stripe */}
        <div className="absolute inset-y-0 left-0 w-1" style={{ background: kind.color }} />

        <div className="flex h-full flex-col justify-between p-2.5 pl-3.5">
          <div className="flex items-start gap-2">
            <div
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-white shadow"
              style={{ background: `linear-gradient(135deg, ${integ?.color ?? kind.color}, ${integ?.color ?? kind.color}aa)` }}
            >
              <def.icon size={15} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold leading-tight text-app">{node.title}</div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="rounded px-1 py-px text-[8.5px] font-bold uppercase tracking-wider" style={{ color: kind.color, background: `${kind.color}1f` }}>
                  {kind.label}
                </span>
                {integ && <span className="truncate text-[10px] text-app-2">{integ.label}</span>}
              </div>
            </div>
            {node.disabled && <span className="text-[9px] font-medium text-app-2">off</span>}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {def.ai && (
                <span className="flex items-center gap-0.5 rounded px-1 py-px text-[9px] font-medium text-violet-400" style={{ background: "#a855f71f" }}>
                  <Sparkles size={9} /> AI
                </span>
              )}
              {(node.approval || def.gate) && (
                <span className="flex items-center gap-0.5 rounded px-1 py-px text-[9px] font-medium text-fuchsia-400" style={{ background: "#d946ef1f" }}>
                  <UserCheck size={9} /> gate
                </span>
              )}
              {showRisk && (
                <span
                  className="flex items-center gap-0.5 rounded px-1 py-px text-[9px] font-medium"
                  style={{ color: RISK_META[def.risk].color, background: `${RISK_META[def.risk].color}1f` }}
                >
                  {def.risk === "restricted" ? "restricted" : def.risk}
                </span>
              )}
            </div>
            {incomplete ? (
              <span className="flex items-center gap-0.5 text-[9px] font-medium text-amber-500">
                <AlertTriangle size={9} /> config
              </span>
            ) : (
              <div className="flex items-center gap-0.5" title="Configuration complete">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="h-1 w-1 rounded-full bg-emerald-500" />
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
